// @flow

import _ from 'lodash';
import React, { Component } from 'react';

import SearchableFolderChooser from 'app/dashboard/components/SearchableFolderChooser';
import SharedAuthsWarningForZapContainer from 'app/dashboard/components/SharedAuthsWarningForZapContainer';
import { defaultIsSectionOpen } from 'app/common/components/Menu';
import { matchesQueryForKeypath } from 'app/common/CommonUtils';

import type { FolderBasicsType, FolderIdType } from 'app/dashboard/types';

export type ZapFolderChooserOnChoose = (
  id: FolderIdType,
  didConfirm: boolean,
  folder: FolderBasicsType
) => void;

type ZapFolderChooserProps = {
  currentFolderIds: Array<number>,
  defaultOpen: 'private' | 'shared',
  folders: any,
  isZapShared: boolean,
  zapId: number,
  onChoose: ZapFolderChooserOnChoose,
  onCreateFolder: (newFolderTitle: string) => Promise<any> | void,
};

type ZapFolderChooserState = Object;

class ZapFolderChooserStateful extends Component<
  ZapFolderChooserProps,
  ZapFolderChooserState
> {
  constructor(props: ZapFolderChooserProps) {
    super(props);

    this.state = {
      choiceArgs: null,
      isWarning: false,
      newFolderTitle: null,
      query: '',
    };
  }

  UNSAFE_componentWillReceiveProps = (nextProps: Object) => {
    if (nextProps.folders.length > this.props.folders.length) {
      this.onWillReceiveNewFolder(nextProps.folders);
    }
  };

  // This is a little funky but is necessary to preserve existing folder
  // moving functionality. The goal is when a new folder has been created
  // we want to move the zap to it.
  //   The reason for this implementation is because `this.props.onChoose`
  // might contain bonus functionality (like changing a parent component's
  // `state`) that we need to preserve, so after we create a folder we
  // _must_ move the zap to the new folder via `this.props.onChoose` in
  // order to preserve that bonus functionality.
  //   In order to not bake promise functionality (i.e. `then`) into this
  // component, compare the new folders we're receiving via `props` to the
  // old ones and see if the newest folder has the title of the one we just
  // created; that's how we know it's the folder that we just created. If
  // it is then we know to move the zap to it.
  onWillReceiveNewFolder = (newFolders: Array<FolderBasicsType>) => {
    // Slightly 👃 but assume the folder with the highest `id` is newest.
    const newestFolder = _.maxBy(newFolders, folder => folder.id);
    // If the newest folder is the one we just created…
    if (newestFolder && newestFolder.title === this.state.newFolderTitle) {
      // Erase the state to prevent confusing ourselves
      this.setState({ newFolderTitle: null });
      // Again, slightly 👃, but manually `onChoose` with this new folder
      // since we want to move the zap to it.
      this.onChoose(newestFolder.id, true, newestFolder);
    }
  };

  onCreateFolder = (newFolderTitle: string) => {
    this.setState({ newFolderTitle });
    this.props.onCreateFolder(newFolderTitle);
  };

  onChoose = (
    id: FolderIdType,
    didConfirm: boolean,
    folder: FolderBasicsType
  ) => {
    if (!didConfirm) {
      return;
    }
    const { isZapShared } = this.props;
    if (folder.isShared && !isZapShared) {
      this.setState({
        isWarning: true,
        // Need to store these for later when the user confirms
        // that they want to choose the folder.
        choiceArgs: [id, didConfirm, folder],
      });
    } else {
      this.props.onChoose(id, didConfirm, folder);
    }
  };

  onConfirmWarning = () => {
    // Shouldn't *need* a condition here, but it's a safeguard
    if (this.state.choiceArgs) {
      this.props.onChoose(...this.state.choiceArgs);
    }
  };

  onCancel = () => {
    this.setState({ isWarning: false });
  };

  onChangeQuery = (query: string) => {
    this.setState({ query });
  };

  onResetQuery = () => {
    this.setState({ query: '' });
  };

  filterFolders = () => {
    const { folders } = this.props;
    const { query } = this.state;
    return folders.filter(_.partial(matchesQueryForKeypath, query, 'title'));
  };

  isSectionOpen = (id: number, openIds: Array<number>) => {
    return this.state.query ? true : defaultIsSectionOpen(id, openIds);
  };

  render() {
    return this.state.isWarning ? (
      <SharedAuthsWarningForZapContainer
        onCancel={this.onCancel}
        onConfirmWarning={this.onConfirmWarning}
        zapId={this.props.zapId}
      />
    ) : (
      <SearchableFolderChooser
        {...this.props}
        canToggleSections={!this.state.query}
        folders={this.filterFolders()}
        isCreating={!!this.state.newFolderTitle}
        isSectionOpen={this.isSectionOpen}
        onChangeQuery={this.onChangeQuery}
        onChoose={this.onChoose}
        onCreateFolder={this.onCreateFolder}
        onResetQuery={this.onResetQuery}
        query={this.state.query}
      />
    );
  }
}

export default ZapFolderChooserStateful;
